import { invoke } from '@tauri-apps/api/core';
import teamVaultService from '../services/teamVaultService';
import { loadRegisteredFolders, registerFolder, FOLDER_REGISTRY_ITEM_NAME, findRegistryItem, normaliseFolderNames } from './vaultFolderRegistry';

/**
 * Deleting a vault folder.
 *
 * THE DEFAULT IS THAT NOTHING IS LOST. Removing a folder removes an
 * ORGANISATIONAL container, not the credentials filed in it: the items come out
 * of the folder and stay in the vault, unfiled. Destroying them is a second,
 * explicitly-opted-into action, because "delete folder" and "delete every
 * password in this folder" are wildly different operations that a single
 * confirmation button should never conflate.
 *
 * The two scopes are genuinely different mechanisms and neither can borrow the
 * other's implementation:
 *
 *   PERSONAL — a folder is the string `data.folder` inside each ENCRYPTED item
 *   body, lifted into a local plaintext index. There is no folder object to
 *   delete. "Deleting" it means clearing that key on every item that carries it
 *   (each one a decrypt → edit → re-encrypt round trip) and dropping the name
 *   from the `__folders__` registry, which is the only place an EMPTY folder
 *   exists at all.
 *
 *   TEAM — a folder is a real server row with its own id and grants. The server
 *   owns the operation: `DELETE .../folders/:id?move_to=root` relocates the
 *   items and removes the row in one authorized step, and REFUSES with a 409 if
 *   the folder still holds something and no destination was given. That refusal
 *   is the server protecting a folder full of production credentials from being
 *   dropped out from under them, so this never passes `move_to` blindly.
 *
 * WHAT IS NOT ATOMIC, stated plainly rather than pretended away: the personal
 * "delete the items too" path is N separate `vault_delete_item` calls. There is
 * no transaction spanning them. An interruption half-way leaves some items
 * deleted and the folder still present — recoverable (re-run it) and visible
 * (the folder is still there, holding fewer things), which is the failure mode
 * worth having. The alternative, deleting the folder first, would strand the
 * survivors somewhere the user can no longer see.
 */

/** Same normalisation `selectFolders` and `derive_index` apply. */
const eq = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/**
 * Personal items filed in `folder`, from the PLAINTEXT index.
 *
 * No decryption: the index is derived locally and already carries the folder
 * name, which is why counting the contents of a folder is free.
 */
export function personalItemsInFolder(items, folder) {
  return (Array.isArray(items) ? items : []).filter((it) => eq(it?.index?.folder, folder));
}

/** Team items filed in `folderId`. Team items carry `folder_id`, not a name. */
export function teamItemsInFolder(items, folderId) {
  const want = String(folderId);
  return (Array.isArray(items) ? items : []).filter((it) => String(it?.folder_id) === want);
}

/** Drop `name` from the `__folders__` registry. Read-modify-write, like registerFolder. */
export async function unregisterFolder(name) {
  const wanted = String(name || '').trim();
  if (!wanted) return loadRegisteredFolders();

  const existing = findRegistryItem(await invoke('vault_list'));
  // No registry means no EMPTY folder was ever recorded — nothing to remove,
  // and creating one here just to delete from it would be absurd.
  if (!existing) return [];

  const current = normaliseFolderNames(
    (await invoke('vault_get_item', { id: existing.id }))?.data?.folders
  );
  const next = current.filter((f) => !eq(f, wanted));
  if (next.length === current.length) return current;

  await invoke('vault_put_item', {
    item: {
      id: existing.id,
      type: 'custom',
      name: FOLDER_REGISTRY_ITEM_NAME,
      data: { folders: next },
    },
  });
  return next;
}

/**
 * Take one personal item out of its folder, keeping everything else about it.
 *
 * A decrypt → edit → re-encrypt round trip, and it MUST preserve every other
 * key in the body: an item written by a newer build carries fields this one has
 * never heard of, and rebuilding the body from known fields would destroy them.
 * Spreading `full.data` is what keeps that promise — the same rule
 * `restFromData` and Rust's `merge_data` follow.
 */
async function unfilePersonalItem(id) {
  const full = await invoke('vault_get_item', { id });
  const data = { ...(full?.data || {}) };
  delete data.folder;

  // PASSKEYS MUST NOT BE SENT BACK, and omitting them is how they are KEPT.
  //
  // `vault_get_item` strips `privateKey`/`prfSecret` from each passkey record
  // but leaves the `passkeys` array in place (`strip_passkey_secrets`). Send
  // that stripped array to `vault_put_item` and `apply_passkey_provenance`
  // compares it against the stored, UNSTRIPPED baseline, sees a difference, and
  // refuses the whole write with `passkeys_not_writable`.
  //
  // Which broke the SAFE path and left only the destructive one working: a
  // folder holding one passkey login could not be deleted at all, however many
  // times you tried, and the way out the UI offers is "also delete the items" —
  // which uses a different command and succeeds. Credentials destroyed to
  // remove a container, with no undo.
  //
  // Deleting the keys is not data loss: the `None` arm of
  // `apply_passkey_provenance` exists for exactly this and re-inserts the
  // stored value, key material intact. `passkeysDeleted` carries the same guard
  // and gets the same treatment.
  delete data.passkeys;
  delete data.passkeysDeleted;

  await invoke('vault_put_item', {
    item: { id, type: full.type, name: full.name, favorite: full.favorite, data },
  });
}

/**
 * Delete a PERSONAL folder.
 *
 * `deleteItems: false` (the default) unfiles everything and keeps it.
 * `deleteItems: true` destroys the items — verified separately by the caller.
 *
 * Returns `{ unfiled, deleted }` so the UI can report what actually happened
 * rather than assuming it matched what was asked.
 */
export async function deletePersonalFolder({ folder, items, deleteItems = false }) {
  const inFolder = personalItemsInFolder(items, folder);
  let unfiled = 0;
  let deleted = 0;

  for (const it of inFolder) {
    if (deleteItems) {
      await invoke('vault_delete_item', { id: it.id });
      deleted += 1;
    } else {
      await unfilePersonalItem(it.id);
      unfiled += 1;
    }
  }

  // LAST, deliberately. If this ran first and the loop then failed, the folder
  // would be gone from the sidebar while its items still carried its name —
  // invisible, and unreachable without re-creating a folder of exactly the same
  // spelling.
  await unregisterFolder(folder);
  return { unfiled, deleted };
}

/**
 * Delete a TEAM folder.
 *
 * Keeping the items is one authorized server call: `move_to=root` relocates
 * them and drops the row together. Destroying them is N deletes and then the
 * folder — and the folder must go LAST for the same reason as above, plus one
 * the server enforces itself: it refuses to drop a folder that still holds
 * something unless told where the contents should go.
 */
export async function deleteTeamFolder({ teamId, folderId, items, deleteItems = false }) {
  if (!deleteItems) {
    await teamVaultService.deleteFolder(teamId, folderId, 'root');
    return { unfiled: teamItemsInFolder(items, folderId).length, deleted: 0 };
  }

  const inFolder = teamItemsInFolder(items, folderId);
  for (const it of inFolder) {
    await teamVaultService.deleteItem(teamId, it.id);
  }
  // No `move_to`: it is empty now, so the server's non-empty guard is satisfied
  // honestly rather than bypassed. If a concurrent write slipped an item in
  // between the loop and here, the server 409s and we have destroyed nothing
  // extra — which is the correct outcome, not a bug to route around.
  await teamVaultService.deleteFolder(teamId, folderId);
  return { unfiled: 0, deleted: inFolder.length };
}

/** Does `secret` open this vault? Confirmation only — see the Rust command. */
export async function verifyMasterPassword(secret) {
  if (!secret) return false;
  try {
    return (await invoke('vault_verify_secret', { secret })) === true;
  } catch {
    // A broken vault is not a wrong password, but neither is it a licence to
    // delete credentials. Fail closed.
    return false;
  }
}

/** Re-exported so the modal does not have to know where folders are recorded. */
export { registerFolder };
